import { FiltersDefinition, SortingsDefinition } from '@factorialco/f0-react'
import { OneDataCollection, SummariesDefinition } from '@factorialco/f0-react/dist/experimental'
import { HeadcountSummariesDocument } from 'generated/resources/graphql'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

type HeadcountRow = {
  parentWithChildren: boolean
  type: string
  name: string
  directManager: string
  role: string
  level: string
  salary: string
  workingTime: string
  startDate: string
  endDate?: string
}

const defaultRow: HeadcountRow = {
  parentWithChildren: false,
  type: '',
  name: '',
  directManager: '',
  role: '',
  level: '',
  salary: '',
  workingTime: '',
  startDate: '',
  endDate: undefined,
}

export const HeadcountEmployeeSheet = ({ periodId }: { periodId: string }) => {
  const source = useGraphqlDataCollectionSource<
    HeadcountRow,
    FiltersDefinition,
    SortingsDefinition,
    SummariesDefinition,
    typeof HeadcountSummariesDocument
  >(
    {
      document: HeadcountSummariesDocument,
      mapData: (data) => {
        const summaries = data.planning.headcountSummariesConnection.nodes
        const newHeadcountCount = summaries[0]?.newHeadcountCount ?? 0
        const actualHeadcountCount = summaries[0]?.actualHeadcountCount ?? 0

        return [
          {
            ...defaultRow,
            parentWithChildren: newHeadcountCount > 0,
            type: i18n.t('planning.employee_sheet.new_headcount'),
            name: `count ${newHeadcountCount}`,
            salary: `sum ${summaries[0]?.newHeadcountTotalMoney ?? '0'}`,
          },
          {
            ...defaultRow,
            parentWithChildren: actualHeadcountCount > 0,
            type: i18n.t('planning.employee_sheet.actual_headcount'),
            name: `count ${actualHeadcountCount}`,
            salary: `sum ${summaries[0]?.actualHeadcountTotalMoney ?? '0'}`,
          },
        ]
      },
      mapVariables: () => ({
        planningPeriodId: String(periodId),
      }),
      mapPagination: () => ({ total: 2 }),
      paginationType: 'pages',
      search: { enabled: true },
      itemsWithChildren: (item) => item.parentWithChildren,
    },
    [periodId]
  )

  return (
    <OneDataCollection
      fullHeight
      source={source}
      visualizations={[
        {
          type: 'table' as const,
          options: {
            columns: [
              {
                label: i18n.t('planning.employee_sheet.columns.type'),
                render: (item) => ({
                  type: 'text' as const,
                  value: item.type,
                }),
              },
              {
                label: i18n.t('planning.employee_sheet.columns.name'),
                render: (item) => ({
                  type: 'text' as const,
                  value: item.name,
                }),
              },
              {
                label: i18n.t('planning.employee_sheet.columns.direct_manager'),
                render: (item) => ({
                  type: 'text' as const,
                  value: item.directManager,
                }),
              },
              {
                label: i18n.t('planning.employee_sheet.columns.role'),
                render: (item) => ({
                  type: 'text' as const,
                  value: item.role,
                }),
              },
              {
                label: i18n.t('planning.employee_sheet.columns.level'),
                render: (item) => ({
                  type: 'text' as const,
                  value: item.level,
                }),
              },
              {
                label: i18n.t('planning.employee_sheet.columns.salary'),
                render: (item) => ({
                  type: 'text' as const,
                  value: item.salary,
                }),
              },
              {
                label: i18n.t('planning.employee_sheet.columns.working_time'),
                render: (item) => ({
                  type: 'text' as const,
                  value: item.workingTime,
                }),
              },
              {
                label: i18n.t('planning.employee_sheet.columns.start_date'),
                render: (item) => ({
                  type: 'text' as const,
                  value: item.startDate,
                }),
              },
              {
                label: i18n.t('planning.employee_sheet.columns.end_date'),
                render: (item) => ({
                  type: 'text' as const,
                  value: item.endDate ?? '',
                }),
              },
            ],
          },
        },
      ]}
      emptyStates={{
        'no-data': {
          title: i18n.t('planning.employee_sheet.actual_headcount_empty.title'),
          description: i18n.t('planning.employee_sheet.actual_headcount_empty.description'),
        },
      }}
    />
  )
}
